// features/profile/edit/IdentityCards.tsx — Edit Profile: Profile Photo & Banner,
// Basic Information, About & Identity (1.27.70, owner concept edit_profile_v2).
import { useRef, useState, type ChangeEvent } from 'react';
import styled from 'styled-components';
import { Avatar, Button } from '@/ui';
import { Icon } from '@/icons';
import { bannerGradient } from '@/theme/presets/identityGradients';
import { buildProfileUrl } from '@/core/lib/postUrl';
import { PROFILE_LIMITS, type BirthdayVisibility, type User } from '@/core/models/account';
import { CardHead, DashCard } from '@/features/settings/dashboard/DashCard';
import type { DraftErrors, ProfileDraftState } from './useProfileDraft';
import { FormField, IconInput } from './fields';
import { Select, TextArea, TextInput } from './fieldStyles';

const MB = 1024 * 1024;
/** Upload caps from backend/apps/media/api.py CAPS. */
const AVATAR_MAX = 2 * MB;
const BANNER_MAX = 5 * MB;
const IMAGE_TYPES = 'image/jpeg,image/png,image/webp,image/gif';

interface CardProps {
  state: ProfileDraftState;
  errors: DraftErrors;
  user: User;
}

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 16px;
`;

const Pair = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  @container (max-width: 619.98px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

// ─── Profile Photo & Banner ──────────────────────────────────────────────────

const Stage = styled.div`
  position: relative;
  margin-top: 16px;
`;

const Banner = styled.div<{ $bg: string }>`
  height: 150px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ $bg }) => $bg};
  overflow: hidden;
`;

const ChangeBanner = styled(Button)`
  position: absolute;
  top: 10px;
  right: 10px;
  background: color-mix(in srgb, ${({ theme }) => theme.color.bgRoot} 72%, transparent);
`;

/* Avatar (overlapping the banner) and the size note side by side, in flow, so the
   note can wrap to any number of lines without running up into the banner. */
const UnderBanner = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
`;

const AvatarSpot = styled.div`
  position: relative;
  flex: none;
  width: 104px;
  margin: -52px 0 0 16px;
  padding: 4px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.surface};
`;

const CameraButton = styled.button`
  position: absolute;
  right: -6px;
  bottom: -6px;
  display: inline-grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  color: ${({ theme }) => theme.color.textPrimary};
  background: ${({ theme }) => theme.color.surfaceRaised};
  box-shadow: 0 0 0 3px ${({ theme }) => theme.color.surface};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const Sizes = styled.p`
  flex: 1;
  min-width: 0;
  margin: 8px 0 0;
  text-align: right;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: ${({ theme }) => theme.lineHeight.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const MediaButtons = styled.div`
  display: grid;
  /* By the card's own width: three across when there is room, otherwise wrap. */
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: 8px;
  margin-top: 16px;
  > button {
    min-width: 0;
    white-space: nowrap;
  }
`;

const MediaError = styled.p`
  margin: 10px 0 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.danger};
`;

function cssBackground(shown: string, isImage: boolean): string {
  return isImage
    ? `center / cover no-repeat url("${shown.replace(/["\\]/g, '')}")`
    : bannerGradient(shown);
}

export function PhotoBannerCard({ state, user }: CardProps) {
  const avatarInput = useRef<HTMLInputElement>(null);
  const bannerInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const { setMedia, avatarShown, bannerShown, hasAvatarImage, hasBannerImage } = state;

  function pick(kind: 'avatar' | 'banner', e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!file) return;
    const max = kind === 'avatar' ? AVATAR_MAX : BANNER_MAX;
    if (!IMAGE_TYPES.split(',').includes(file.type)) {
      setError('Choose a JPG, PNG, WebP or GIF image.');
      return;
    }
    if (file.size > max) {
      setError(`That image is too large (max ${max / MB} MB).`);
      return;
    }
    setError(null);
    setMedia((m) =>
      kind === 'avatar'
        ? { ...m, avatarFile: file, clearAvatarImage: false }
        : { ...m, bannerFile: file, clearBannerImage: false },
    );
  }

  return (
    <DashCard labelledBy="ep-photo-title" id="photo" testId="photo-card">
      <CardHead
        icon="camera"
        title="Profile Photo & Banner"
        titleId="ep-photo-title"
        subtitle="Show your personality with a profile photo and banner."
        divided={false}
      />
      <Stage>
        <Banner
          $bg={cssBackground(bannerShown, hasBannerImage)}
          role="img"
          aria-label={hasBannerImage ? 'Your banner image' : 'Your banner colour'}
        />
        <ChangeBanner variant="secondary" size="sm" onClick={() => bannerInput.current?.click()}>
          <Icon name="image" size={14} />
          Change banner
        </ChangeBanner>
      </Stage>
      <UnderBanner>
        <AvatarSpot>
          <Avatar name={user.name} avatarKey={avatarShown} size={96} />
          <CameraButton
            type="button"
            aria-label="Change profile photo"
            onClick={() => avatarInput.current?.click()}
          >
            <Icon name="camera" size={16} />
          </CameraButton>
        </AvatarSpot>
        <Sizes>
          Banner: 1500 × 500 px recommended, up to 5 MB. Photo: square, up to 2 MB. JPG, PNG, WebP
          or GIF.
        </Sizes>
      </UnderBanner>
      <input
        ref={avatarInput}
        type="file"
        accept={IMAGE_TYPES}
        hidden
        data-testid="edit-avatar-input"
        onChange={(e) => pick('avatar', e)}
      />
      <input
        ref={bannerInput}
        type="file"
        accept={IMAGE_TYPES}
        hidden
        data-testid="edit-banner-input"
        onChange={(e) => pick('banner', e)}
      />
      <MediaButtons>
        <Button variant="secondary" size="sm" onClick={() => avatarInput.current?.click()}>
          <Icon name="camera" size={14} />
          Change photo
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!hasAvatarImage}
          onClick={() => setMedia((m) => ({ ...m, avatarFile: null, clearAvatarImage: true }))}
        >
          <Icon name="trash" size={14} />
          Remove photo
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!hasBannerImage}
          onClick={() => setMedia((m) => ({ ...m, bannerFile: null, clearBannerImage: true }))}
        >
          <Icon name="trash" size={14} />
          Remove banner
        </Button>
      </MediaButtons>
      {error && <MediaError role="alert">{error}</MediaError>}
    </DashCard>
  );
}

// ─── Basic Information ───────────────────────────────────────────────────────

const PRONOUN_OPTIONS = ['She/her', 'He/him', 'They/them', 'She/they', 'He/they', 'Any pronouns'];
const CREATOR_TYPES = [
  'Creator',
  'Writer',
  'Journalist',
  'Artist',
  'Musician',
  'Photographer',
  'Filmmaker',
  'Podcaster',
  'Streamer',
  'Developer',
  'Educator',
  'Researcher',
  'Business',
  'Organization',
  'Community',
  'Public figure',
];
const CUSTOM = '__custom__';

/** A select of common choices plus "Something else", which opens a text box. */
function ChoiceOrCustom({
  id,
  describedBy,
  invalid,
  value,
  options,
  noneLabel,
  customLabel,
  maxLength,
  onChange,
}: {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
  value: string;
  options: string[];
  noneLabel: string;
  customLabel: string;
  maxLength: number;
  onChange: (next: string) => void;
}) {
  const [custom, setCustom] = useState(() => value !== '' && !options.includes(value));
  const selectValue = custom ? CUSTOM : value;
  return (
    <>
      <Select
        id={id}
        aria-describedby={describedBy}
        $invalid={invalid}
        value={selectValue}
        onChange={(e) => {
          if (e.target.value === CUSTOM) {
            setCustom(true);
            onChange('');
          } else {
            setCustom(false);
            onChange(e.target.value);
          }
        }}
      >
        <option value="">{noneLabel}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
        <option value={CUSTOM}>Something else…</option>
      </Select>
      {custom && (
        <TextInput
          aria-label={customLabel}
          $invalid={invalid}
          value={value}
          maxLength={maxLength}
          placeholder={customLabel}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </>
  );
}

export function BasicInfoCard({ state, errors, user }: CardProps) {
  const { draft, set } = state;
  return (
    <DashCard labelledBy="ep-basic-title" id="basic" testId="basic-card">
      <CardHead
        icon="user"
        title="Basic Information"
        titleId="ep-basic-title"
        subtitle="Tell people who you are."
      />
      <Fields>
        <FormField
          label="Display name"
          required
          error={errors.displayName}
          count={{ value: draft.displayName.length, max: PROFILE_LIMITS.displayName }}
        >
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              aria-required="true"
              $invalid={invalid}
              value={draft.displayName}
              maxLength={PROFILE_LIMITS.displayName}
              onChange={(e) => set('displayName', e.target.value)}
            />
          )}
        </FormField>
        <FormField
          label="Handle"
          hint={`${buildProfileUrl(user.handle)}. Handles can't be changed.`}
        >
          {({ id, describedBy }) => (
            <TextInput id={id} aria-describedby={describedBy} value={user.handle} readOnly />
          )}
        </FormField>
        <FormField
          label="Bio"
          error={errors.bio}
          count={{ value: draft.bio.length, max: PROFILE_LIMITS.bio }}
        >
          {({ id, describedBy, invalid }) => (
            <TextArea
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              $invalid={invalid}
              rows={3}
              value={draft.bio}
              maxLength={PROFILE_LIMITS.bio}
              placeholder="Tell people about yourself"
              onChange={(e) => set('bio', e.target.value)}
            />
          )}
        </FormField>
        <FormField
          label="Pronouns"
          hint="Help others refer to you correctly."
          error={errors.pronouns}
        >
          {(ids) => (
            <ChoiceOrCustom
              {...ids}
              value={draft.pronouns}
              options={PRONOUN_OPTIONS}
              noneLabel="Don't show"
              customLabel="Your pronouns"
              maxLength={PROFILE_LIMITS.pronouns}
              onChange={(v) => set('pronouns', v)}
            />
          )}
        </FormField>
        <FormField
          label="Creator type or category"
          hint="Helps people understand what you do."
          error={errors.creatorType}
        >
          {(ids) => (
            <ChoiceOrCustom
              {...ids}
              value={draft.creatorType}
              options={CREATOR_TYPES}
              noneLabel="None"
              customLabel="What you do"
              maxLength={PROFILE_LIMITS.creatorType}
              onChange={(v) => set('creatorType', v)}
            />
          )}
        </FormField>
      </Fields>
    </DashCard>
  );
}

// ─── About & Identity ────────────────────────────────────────────────────────

const VISIBILITY_LABELS: Record<BirthdayVisibility, string> = {
  private: 'Only you',
  month_day: 'Month and day',
  full: 'Full date',
};

export function AboutIdentityCard({ state, errors }: CardProps) {
  const { draft, set } = state;
  return (
    <DashCard labelledBy="ep-about-title" id="about" testId="about-card">
      <CardHead
        icon="file-text"
        title="About & Identity"
        titleId="ep-about-title"
        subtitle="Share more details about yourself."
      />
      <Fields>
        <Pair>
          <FormField label="Location">
            {({ id, describedBy }) => (
              <IconInput
                id={id}
                aria-describedby={describedBy}
                icon="map-pin"
                value={draft.location}
                maxLength={120}
                placeholder="Where you're based"
                clearLabel="Clear location"
                onClear={() => set('location', '')}
                onChange={(e) => set('location', e.target.value)}
              />
            )}
          </FormField>
          <FormField label="Website" error={errors.website}>
            {({ id, describedBy, invalid }) => (
              <IconInput
                id={id}
                aria-describedby={describedBy}
                icon="link"
                invalid={invalid}
                inputMode="url"
                value={draft.website}
                maxLength={PROFILE_LIMITS.linkHref}
                placeholder="example.org"
                clearLabel="Clear website"
                onClear={() => set('website', '')}
                onChange={(e) => set('website', e.target.value)}
              />
            )}
          </FormField>
        </Pair>
        <FormField
          label="Tagline"
          hint="One line, shown above your bio."
          error={errors.tagline}
          count={{ value: draft.tagline.length, max: PROFILE_LIMITS.tagline }}
        >
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              $invalid={invalid}
              value={draft.tagline}
              maxLength={PROFILE_LIMITS.tagline}
              placeholder="Your line in a sentence"
              onChange={(e) => set('tagline', e.target.value)}
            />
          )}
        </FormField>
        <Pair>
          <FormField
            label="Birthday"
            error={errors.birthday}
            hint={
              draft.birthdayVisibility === 'private'
                ? "Your birthday won't be shown on your profile."
                : draft.birthdayVisibility === 'month_day'
                  ? 'Your profile shows the month and day, never the year.'
                  : 'Your profile shows the full date.'
            }
          >
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                type="date"
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                $invalid={invalid}
                value={draft.birthday}
                min="1900-01-01"
                onChange={(e) => set('birthday', e.target.value)}
              />
            )}
          </FormField>
          <FormField label="Who sees it">
            {({ id }) => (
              <Select
                id={id}
                value={draft.birthdayVisibility}
                disabled={!draft.birthday}
                onChange={(e) => set('birthdayVisibility', e.target.value as BirthdayVisibility)}
              >
                {(Object.keys(VISIBILITY_LABELS) as BirthdayVisibility[]).map((v) => (
                  <option key={v} value={v}>
                    {VISIBILITY_LABELS[v]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        </Pair>
      </Fields>
    </DashCard>
  );
}
